import styled, { keyframes } from 'styled-components';

// ============================================
// ANIMATIONS
// ============================================

export const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

export const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: linear-gradient(180deg, #0f1419 0%, #1a1f2e 100%);
  color: #e8eaed;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

export const Header = styled.div`
  padding: 20px 24px;
  background: rgba(30, 35, 50, 0.95);
  border-bottom: 1px solid rgba(100, 150, 200, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

export const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
  margin-right: 20px;
`;

export const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex-wrap: wrap;
`;

export const HeaderBadge = styled.span`
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%);
  border: 1px solid rgba(99, 102, 241, 0.25);
  color: #a5b4fc;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  max-width: 500px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
`;

export const HeaderSubtitle = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;

export const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  .indicator {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${props => {
      switch(props.$status) {
        case 'green': return '#10b981';
        case 'yellow': return '#f59e0b';
        case 'red': return '#ef4444';
        default: return '#6b7280';
      }
    }};
    box-shadow: 0 0 8px ${props => {
      switch(props.$status) {
        case 'green': return 'rgba(16, 185, 129, 0.5)';
        case 'yellow': return 'rgba(245, 158, 11, 0.5)';
        case 'red': return 'rgba(239, 68, 68, 0.5)';
        default: return 'rgba(107, 114, 128, 0.5)';
      }
    }};
  }
  
  span {
    font-size: 13px;
    font-weight: 500;
    color: ${props => {
      switch(props.$status) {
        case 'green': return '#34d399';
        case 'yellow': return '#fbbf24';
        case 'red': return '#f87171';
        default: return '#9ca3af';
      }
    }};
  }
`;

export const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  color: #a5b4fc;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(99, 102, 241, 0.25) 100%);
    border-color: rgba(99, 102, 241, 0.5);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

export const SpinningIcon = styled.svg`
  animation: ${spin} 1s linear infinite;
`;

export const LastUpdated = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
`;

export const MainContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(100, 150, 200, 0.3);
    border-radius: 4px;
  }
`;

// Empty State
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  padding: 40px;
  
  svg {
    width: 64px;
    height: 64px;
    color: rgba(99, 102, 241, 0.4);
    margin-bottom: 20px;
  }
  
  h3 {
    margin: 0 0 10px 0;
    font-size: 18px;
    font-weight: 600;
    color: #fff;
  }
  
  p {
    margin: 0 0 24px 0;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.5);
    max-width: 400px;
  }
`;

export const AnalyzeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(99, 102, 241, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

export const LoadingSpinner = styled.div`
  width: ${props => props.$size || 20}px;
  height: ${props => props.$size || 20}px;
  border: 2px solid rgba(99, 102, 241, 0.2);
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

// Section styles
export const Section = styled.div`
  background: rgba(30, 35, 50, 0.6);
  border: 1px solid rgba(100, 150, 200, 0.15);
  border-radius: 16px;
  margin-bottom: 20px;
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease;
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  background: rgba(25, 30, 45, 0.8);
  border-bottom: 1px solid rgba(100, 150, 200, 0.1);
  
  svg {
    width: 18px;
    height: 18px;
    color: #6366f1;
  }
  
  h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #fff;
  }
`;

export const SectionContent = styled.div`
  padding: 16px 18px;
`;

// Grid layouts
export const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const ThreeColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

// Cards
export const InfoCard = styled.div`
  background: rgba(40, 45, 65, 0.5);
  border: 1px solid rgba(100, 150, 200, 0.1);
  border-radius: 10px;
  padding: 14px;
`;

export const CardLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

export const CardValue = styled.div`
  font-size: 13px;
  color: #e8eaed;
  line-height: 1.5;
`;

// Client Profile Grid - 4 columns for compact display
export const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const ProfileField = styled.div`
  background: rgba(40, 45, 65, 0.4);
  border: 1px solid rgba(100, 150, 200, 0.08);
  border-radius: 8px;
  padding: 10px 12px;
  
  &.highlight {
    background: rgba(99, 102, 241, 0.1);
    border-color: rgba(99, 102, 241, 0.2);
  }
  
  &.contact {
    background: rgba(16, 185, 129, 0.08);
    border-color: rgba(16, 185, 129, 0.15);
  }
`;

export const ProfileLabel = styled.div`
  font-size: 9px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

export const ProfileValue = styled.div`
  font-size: 13px;
  color: #e8eaed;
  font-weight: 500;
  word-break: break-word;
  
  &.missing {
    color: rgba(255, 255, 255, 0.3);
    font-style: italic;
    font-weight: 400;
  }
  
  &.email {
    color: #60a5fa;
  }
  
  &.phone {
    color: #34d399;
  }
`;

// Quick stats
export const QuickStatCard = styled.div`
  background: rgba(40, 45, 65, 0.5);
  border: 1px solid rgba(100, 150, 200, 0.1);
  border-radius: 10px;
  padding: 14px;
  
  h5 {
    margin: 0 0 10px 0;
    font-size: 11px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
  }
`;

export const BlockerList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  
  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 12px;
    color: #f87171;
    padding: 4px 0;
    
    &::before {
      content: '!';
      display: flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      background: rgba(239, 68, 68, 0.2);
      border-radius: 50%;
      font-size: 10px;
      font-weight: 700;
      flex-shrink: 0;
    }
  }
`;

export const RiskList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  
  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 12px;
    color: #fbbf24;
    padding: 4px 0;
    
    &::before {
      content: '⚠';
      font-size: 11px;
      flex-shrink: 0;
    }
  }
`;

// Pathway cards
export const PathwaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
`;

export const PathwayCard = styled.div`
  background: rgba(40, 45, 65, 0.6);
  border: 1px solid ${props => props.$selected ? 'rgba(99, 102, 241, 0.5)' : 'rgba(100, 150, 200, 0.15)'};
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => {
      const score = props.$score || 0;
      if (score >= 70) return 'linear-gradient(90deg, #10b981, #34d399)';
      if (score >= 40) return 'linear-gradient(90deg, #f59e0b, #fbbf24)';
      return 'linear-gradient(90deg, #ef4444, #f87171)';
    }};
  }
  
  &:hover {
    border-color: rgba(99, 102, 241, 0.3);
    transform: translateY(-2px);
  }
`;

export const PathwayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;
`;

export const PathwayTitle = styled.div`
  display: flex;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  
  .flag { font-size: 20px; margin-right: 8px; flex-shrink: 0; }
  .country { font-size: 10px; color: rgba(255,255,255,0.5); text-transform: uppercase; }
  h5 { margin: 4px 0 0 0; font-size: 13px; font-weight: 600; color: #fff; word-break: break-word; }
`;

export const ScoreCircle = styled.div`
  width: 48px;
  min-width: 48px;
  height: 48px;
  min-height: 48px;
  border-radius: 50%;
  flex-shrink: 0;
  box-sizing: border-box;
  background: ${props => {
    const score = props.$score || 0;
    if (score >= 70) return 'rgba(16, 185, 129, 0.15)';
    if (score >= 40) return 'rgba(245, 158, 11, 0.15)';
    return 'rgba(239, 68, 68, 0.15)';
  }};
  border: 2px solid ${props => {
    const score = props.$score || 0;
    if (score >= 70) return '#10b981';
    if (score >= 40) return '#f59e0b';
    return '#ef4444';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: ${props => {
    const score = props.$score || 0;
    if (score >= 70) return '#34d399';
    if (score >= 40) return '#fbbf24';
    return '#f87171';
  }};
`;

export const PathwaySummary = styled.p`
  margin: 0 0 12px 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
`;

export const ConfidenceBadge = styled.span`
  display: inline-flex;
  padding: 3px 8px;
  background: ${props => props.$level === 'high' ? 'rgba(16, 185, 129, 0.15)' : props.$level === 'medium' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(239, 68, 68, 0.15)'};
  border: 1px solid ${props => props.$level === 'high' ? 'rgba(16, 185, 129, 0.3)' : props.$level === 'medium' ? 'rgba(245, 158, 11, 0.3)' : 'rgba(239, 68, 68, 0.3)'};
  border-radius: 10px;
  font-size: 10px;
  font-weight: 500;
  color: ${props => props.$level === 'high' ? '#34d399' : props.$level === 'medium' ? '#fbbf24' : '#f87171'};
  margin-bottom: 10px;
`;

export const ProsCons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  
  .pros h6, .cons h6 {
    margin: 0 0 6px 0;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .pros h6 { color: #34d399; }
  .cons h6 { color: #f87171; }
  
  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    
    li {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.7);
      padding: 2px 0;
      display: flex;
      align-items: flex-start;
      gap: 4px;
    }
  }
  .pros li::before { content: '+'; color: #34d399; font-weight: 700; }
  .cons li::before { content: '-'; color: #f87171; font-weight: 700; }
`;

// Score breakdown
export const BreakdownGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
`;

export const BreakdownItem = styled.div`
  .label {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 6px;
    display: flex;
    justify-content: space-between;
    
    span {
      font-weight: 600;
      color: ${props => props.$score >= 70 ? '#34d399' : props.$score >= 40 ? '#fbbf24' : '#f87171'};
    }
  }
  
  .bar {
    height: 5px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
    overflow: hidden;
    
    .fill {
      height: 100%;
      width: ${props => props.$score || 0}%;
      background: ${props => props.$score >= 70 ? 'linear-gradient(90deg, #10b981, #34d399)' : props.$score >= 40 ? 'linear-gradient(90deg, #f59e0b, #fbbf24)' : 'linear-gradient(90deg, #ef4444, #f87171)'};
      transition: width 0.5s ease;
    }
  }
`;

// Action items
export const ActionItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ActionItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: rgba(99, 102, 241, 0.06);
  border: 1px solid rgba(99, 102, 241, 0.12);
  border-radius: 8px;
  
  .text {
    font-size: 12px;
    color: #e8eaed;
  }
  
  .priority {
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    background: ${props => props.$priority === 'critical' ? 'rgba(239, 68, 68, 0.2)' : props.$priority === 'high' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(107, 114, 128, 0.2)'};
    color: ${props => props.$priority === 'critical' ? '#f87171' : props.$priority === 'high' ? '#fbbf24' : '#9ca3af'};
  }
`;

// Missing items
export const MissingItemsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const MissingItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  background: ${props => props.$resolved ? 'rgba(16, 185, 129, 0.1)' : 'rgba(0, 0, 0, 0.2)'};
  border-radius: 8px;
  border: 1px solid ${props => props.$resolved ? 'rgba(16, 185, 129, 0.3)' : 'transparent'};
  opacity: ${props => props.$resolved ? 0.7 : 1};
  transition: all 0.2s ease;
  
  .checkbox-col {
    flex-shrink: 0;
    padding-top: 2px;
  }
  
  .content-col {
    flex: 1;
    min-width: 0;
    
    .top-row {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 6px;
    }
    
    .priority {
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 9px;
      font-weight: 600;
      text-transform: uppercase;
      flex-shrink: 0;
      background: ${props => props.$resolved ? 'rgba(16, 185, 129, 0.2)' : props.$priority === 'blocker' ? 'rgba(239, 68, 68, 0.2)' : props.$priority === 'important' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(107, 114, 128, 0.2)'};
      color: ${props => props.$resolved ? '#34d399' : props.$priority === 'blocker' ? '#f87171' : props.$priority === 'important' ? '#fbbf24' : '#9ca3af'};
    }
    
    .name { 
      font-size: 13px; 
      font-weight: 500;
      color: #fff;
      text-decoration: ${props => props.$resolved ? 'line-through' : 'none'};
      opacity: ${props => props.$resolved ? 0.7 : 1};
    }
    
    .question {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.45);
      font-style: italic;
      line-height: 1.4;
    }
  }
`;

// Resolution checkbox
export const ResolutionCheckbox = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 2px solid ${props => props.$checked ? '#10b981' : 'rgba(255, 255, 255, 0.2)'};
  background: ${props => props.$checked ? 'rgba(16, 185, 129, 0.2)' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 10px;
  flex-shrink: 0;
  
  &:hover {
    border-color: ${props => props.$checked ? '#34d399' : 'rgba(255, 255, 255, 0.4)'};
    background: ${props => props.$checked ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.05)'};
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  
  svg {
    width: 16px;
    height: 16px;
    color: ${props => props.$checked ? '#10b981' : 'transparent'};
  }
  
  .spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-top-color: #10b981;
    border-radius: 50%;
    animation: ${spin} 0.6s linear infinite;
  }
`;

// Resolved badge
export const ResolvedBadge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  background: rgba(16, 185, 129, 0.2);
  color: #34d399;
  margin-left: 8px;
`;

// Next steps
export const NextStepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const NextStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: rgba(16, 185, 129, 0.06);
  border-radius: 8px;
  
  .number {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: linear-gradient(135deg, #10b981, #34d399);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    color: white;
    flex-shrink: 0;
  }
  
  .content {
    flex: 1;
    
    .description { font-size: 12px; color: #e8eaed; line-height: 1.5; }
    .timeframe { font-size: 11px; color: rgba(255, 255, 255, 0.5); margin-top: 4px; }
  }
`;

// Notes
export const NoteItem = styled.div`
  padding: 10px 14px;
  background: rgba(99, 102, 241, 0.05);
  border-left: 3px solid #6366f1;
  border-radius: 0 8px 8px 0;
  margin-bottom: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
  
  &:last-child { margin-bottom: 0; }
`;

// ============================================
// FIRM STANDARDS COMPLIANCE STYLED COMPONENTS
// ============================================

export const ComplianceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
`;

export const ComplianceCard = styled.div`
  background: ${props => {
    switch(props.$status) {
      case 'compliant': return 'rgba(16, 185, 129, 0.08)';
      case 'violation': return 'rgba(239, 68, 68, 0.08)';
      case 'warning': return 'rgba(245, 158, 11, 0.08)';
      case 'not_applicable': return 'rgba(107, 114, 128, 0.08)';
      default: return 'rgba(99, 102, 241, 0.08)';
    }
  }};
  border: 1px solid ${props => {
    switch(props.$status) {
      case 'compliant': return 'rgba(16, 185, 129, 0.25)';
      case 'violation': return 'rgba(239, 68, 68, 0.25)';
      case 'warning': return 'rgba(245, 158, 11, 0.25)';
      case 'not_applicable': return 'rgba(107, 114, 128, 0.2)';
      default: return 'rgba(99, 102, 241, 0.2)';
    }
  }};
  border-radius: 10px;
  padding: 14px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

export const ComplianceHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

export const ComplianceSectionName = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${props => {
    switch(props.$status) {
      case 'compliant': return '#34d399';
      case 'violation': return '#f87171';
      case 'warning': return '#fbbf24';
      case 'not_applicable': return '#9ca3af';
      default: return '#a5b4fc';
    }
  }};
`;

export const ComplianceStatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  
  background: ${props => {
    switch(props.$status) {
      case 'compliant': return 'rgba(16, 185, 129, 0.2)';
      case 'violation': return 'rgba(239, 68, 68, 0.2)';
      case 'warning': return 'rgba(245, 158, 11, 0.2)';
      case 'not_applicable': return 'rgba(107, 114, 128, 0.2)';
      default: return 'rgba(99, 102, 241, 0.2)';
    }
  }};
  color: ${props => {
    switch(props.$status) {
      case 'compliant': return '#34d399';
      case 'violation': return '#f87171';
      case 'warning': return '#fbbf24';
      case 'not_applicable': return '#9ca3af';
      default: return '#a5b4fc';
    }
  }};
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

export const ComplianceDetail = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
  margin-bottom: 8px;
  
  &:last-child { margin-bottom: 0; }
`;

export const ComplianceIssuesList = styled.ul`
  margin: 8px 0 0 0;
  padding: 0 0 0 16px;
  
  li {
    font-size: 11px;
    color: ${props => props.$isWarning ? '#fbbf24' : '#f87171'};
    padding: 2px 0;
    line-height: 1.4;
  }
`;

export const ComplianceSummaryBar = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px;
  background: rgba(30, 35, 50, 0.6);
  border-radius: 10px;
  margin-bottom: 16px;
`;

export const ComplianceStat = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  .icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    
    background: ${props => {
      switch(props.$type) {
        case 'compliant': return 'rgba(16, 185, 129, 0.15)';
        case 'violation': return 'rgba(239, 68, 68, 0.15)';
        case 'warning': return 'rgba(245, 158, 11, 0.15)';
        default: return 'rgba(107, 114, 128, 0.15)';
      }
    }};
    
    svg {
      width: 16px;
      height: 16px;
      color: ${props => {
        switch(props.$type) {
          case 'compliant': return '#34d399';
          case 'violation': return '#f87171';
          case 'warning': return '#fbbf24';
          default: return '#9ca3af';
        }
      }};
    }
  }
  
  .info {
    .count {
      font-size: 18px;
      font-weight: 700;
      color: ${props => {
        switch(props.$type) {
          case 'compliant': return '#34d399';
          case 'violation': return '#f87171';
          case 'warning': return '#fbbf24';
          default: return '#9ca3af';
        }
      }};
    }
    .label {
      font-size: 10px;
      color: rgba(255, 255, 255, 0.5);
      text-transform: uppercase;
    }
  }
`;

// Client communication
export const CommCard = styled.div`
  background: rgba(40, 45, 65, 0.5);
  border: 1px solid rgba(100, 150, 200, 0.1);
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 12px;
  
  &:last-child { margin-bottom: 0; }
  
  h6 {
    margin: 0 0 10px 0;
    font-size: 11px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
  }
  
  p {
    margin: 0;
    font-size: 12px;
    color: #e8eaed;
    line-height: 1.5;
  }
  
  ul {
    margin: 0;
    padding: 0 0 0 16px;
    
    li {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.8);
      padding: 3px 0;
    }
  }
`;

// ============================================
// SHARE MODAL STYLED COMPONENTS
// ============================================

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
`;

export const ModalContent = styled.div`
  background: linear-gradient(180deg, #1a1f2e 0%, #0f1419 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.2s ease;
`;

export const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 10px;
    
    svg {
      width: 22px;
      height: 22px;
      color: #a5b4fc;
    }
  }
`;

export const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  
  &:hover {
    color: #fff;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

export const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

export const SectionSelector = styled.div`
  margin-bottom: 24px;
  
  h4 {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.7);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

export const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
`;

export const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(99, 102, 241, 0.15);
    border-color: rgba(99, 102, 241, 0.3);
  }
  
  input {
    width: 18px;
    height: 18px;
    accent-color: #6366f1;
    cursor: pointer;
  }
  
  span {
    font-size: 13px;
    color: #e8eaed;
  }
  
  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const ShareOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

export const ShareOptionButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 20px 16px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(99, 102, 241, 0.2) 100%);
    border-color: rgba(99, 102, 241, 0.4);
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  svg {
    width: 28px;
    height: 28px;
    color: #a5b4fc;
  }
  
  span {
    font-size: 13px;
    font-weight: 500;
    color: #e8eaed;
  }
  
  small {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.5);
  }
`;

export const SelectAllRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  
  button {
    background: transparent;
    border: 1px solid rgba(99, 102, 241, 0.3);
    color: #a5b4fc;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover {
      background: rgba(99, 102, 241, 0.15);
      border-color: rgba(99, 102, 241, 0.5);
    }
  }
`;

export const ShareButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(34, 197, 94, 0.15) 100%);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 8px;
  color: #86efac;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.25) 0%, rgba(34, 197, 94, 0.25) 100%);
    border-color: rgba(34, 197, 94, 0.5);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

export const CopiedToast = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  z-index: 10001;
  display: flex;
  align-items: center;
  gap: 8px;
  animation: ${fadeIn} 0.2s ease;
  box-shadow: 0 4px 20px rgba(16, 185, 129, 0.4);
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

export const SettingsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  background: linear-gradient(135deg, rgba(107, 114, 128, 0.15) 0%, rgba(75, 85, 99, 0.15) 100%);
  border: 1px solid rgba(107, 114, 128, 0.3);
  border-radius: 8px;
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(107, 114, 128, 0.25) 0%, rgba(75, 85, 99, 0.25) 100%);
    border-color: rgba(107, 114, 128, 0.5);
    color: #e5e7eb;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

export const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(75, 85, 99, 0.5);
    transition: 0.3s;
    border-radius: 24px;
    
    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: 0.3s;
      border-radius: 50%;
    }
  }
  
  input:checked + span {
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  }
  
  input:checked + span:before {
    transform: translateX(20px);
  }
`;

export const SettingsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
  
  .label {
    display: flex;
    align-items: center;
    gap: 10px;
    
    svg {
      width: 18px;
      height: 18px;
      color: #a5b4fc;
    }
    
    span {
      font-size: 14px;
      color: #e8eaed;
    }
  }
`;

export const SettingsCategory = styled.div`
  margin-bottom: 20px;
  
  h5 {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.5);
    margin: 0 0 12px 0;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(99, 102, 241, 0.2);
  }
`;
